import {
  Box,
  Image,
  Table,
  TableContainer,
  Tbody,
  Thead,
  Tr,
  Th,
  Td,
  Flex,
  Skeleton,
  VStack,
  HStack,
  Text,
  Heading,
  useColorModeValue,
  SimpleGrid,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  IconButton,
  Input,
  Button,
  Select,
  FormControl,
  FormLabel,
  Collapse,
} from "@chakra-ui/react";
import React, { useState, useMemo } from "react";
import picAvatar from "../assets/picAvatar.svg";
import ListOfEmployeeComponent from "./ListOfEmployeeComponent";
import { useNavigate } from "react-router-dom";
import { stringifyData } from "../../utils";
import { useDeleteEmployee, useRestoreEmployee } from "../../services/employeeService";
import { useGetPlanInfo } from "../../services/planService";
import { formatCurrency } from "../../utils/currency";
import { FiFilter, FiSearch, FiX, FiChevronDown, FiChevronRight, FiMail, FiPhone, FiMapPin, FiCalendar, FiDollarSign, FiUser, FiEdit, FiTrash2, FiHome, FiRefreshCw } from "react-icons/fi";
import AvatarComponent from "../avatar";

const EmployeeTable = ({ employeeData = [], getEmployeesIsLoading, refetchGetEmployees, statusFilter = "active" }) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");
  
  // Get plan info for country-based currency
  const { planInfo } = useGetPlanInfo();
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Debug logging


  const handleEmployeeClick = (item) => {
    sessionStorage.setItem("employee", stringifyData(item))
    navigate(`/employee/view-employee/${item?.employee_id}`);
  };

  const handleEdit = (item) => {
    sessionStorage.setItem("todo","edit")
    sessionStorage.setItem("employee", stringifyData(item))
    navigate(`/employee/add-employee?id=${item?.employee_id}`);
  }

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = employeeData || [];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        `${item?.first_name} ${item?.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Department filter
    if (departmentFilter) {
      filtered = filtered.filter(item => 
        item?.department === departmentFilter
      );
    }
    
    // Status filter - filter by passed statusFilter prop
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter(item => 
        item?.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }
    
    return filtered;
  }, [employeeData, searchTerm, departmentFilter, statusFilter]);

  // Get unique departments for filter dropdown
  const departments = useMemo(() => {
    if (!employeeData) return [];
    const depts = [...new Set(employeeData.map(item => item?.department).filter(Boolean))];
    return depts.sort();
  }, [employeeData]);

  const clearFilters = () => {
    setSearchTerm("");
    setDepartmentFilter("");
  };

  const toggleRowExpansion = (employeeId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(employeeId)) {
      newExpandedRows.delete(employeeId);
    } else {
      newExpandedRows.add(employeeId);
    }
    setExpandedRows(newExpandedRows);
  };

  if (getEmployeesIsLoading) {
    return (
      <Box p={6}>
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th></Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>ID</Th>
                <Th>Department</Th>
                <Th>Salary</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <Tr key={i}>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (!getEmployeesIsLoading && !employeeData?.length) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Box
            p={4}
            borderRadius="50%"
            bg="gray.100"
            color="gray.500"
            fontSize="2xl"
          >
            👥
          </Box>
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="600" color="gray.700">
              No Employees Found
            </Text>
            <Text color="gray.500" fontSize="sm">
              No employee records found. Add your first employee to get started.
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }


  return (
    <Box>
      {/* Table Header */}
      <Flex 
        justifyContent="space-between" 
        alignItems="center" 
        p={6} 
        borderBottom="1px solid" 
        borderColor="gray.200"
        bg={bgColor}
      >
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Employee Directory
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {filteredData?.length || 0} of {employeeData?.length || 0} employees
          </Text>
        </VStack>
        <IconButton
          icon={<FiFilter />}
          variant="ghost"
          onClick={onOpen}
          aria-label="Filter employees"
          color="gray.600"
          _hover={{ bg: "gray.100" }}
        />
      </Flex>

      {/* Search Bar */}
      <Box p={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
        <HStack spacing={4} align="center">
          <Box flex={1}>
            <HStack spacing={2}>
              <Box position="relative" flex={1}>
                <Input
                  placeholder="Search by name, email, ID, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  pl={10}
                  pr={searchTerm ? 10 : 4}
                  borderRadius="12px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
                  <FiSearch size={16} />
                </Box>
                {searchTerm && (
                  <Box 
                    position="absolute" 
                    right={3} 
                    top="50%" 
                    transform="translateY(-50%)" 
                    cursor="pointer"
                    onClick={() => setSearchTerm("")}
                    color="gray.400"
                    _hover={{ color: "gray.600" }}
                  >
                    <FiX size={16} />
                  </Box>
                )}
              </Box>
              {(searchTerm || departmentFilter || statusFilter) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearFilters}
                  leftIcon={<FiX />}
                  color="gray.600"
                  _hover={{ bg: "gray.200" }}
                >
                  Clear
                </Button>
              )}
            </HStack>
          </Box>
        </HStack>
      </Box>

      {/* Employee Table */}
      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th></Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>ID</Th>
              <Th>Department</Th>
              <Th>Salary</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData?.map((item, index) => (
              <React.Fragment key={index}>
                <EmployeeTableRow 
                  {...item} 
                  isExpanded={expandedRows.has(item.employee_id)}
                  onToggle={() => toggleRowExpansion(item.employee_id)}
                  onEdit={() => handleEdit(item)}
                  onView={() => handleEmployeeClick(item)}
                  refetchGetEmployees={refetchGetEmployees}
                  planInfo={planInfo}
                />
                <Tr>
                  <Td colSpan={8} p={0}>
                    <Collapse in={expandedRows.has(item.employee_id)} animateOpacity>
                      <Box bg="gray.50" p={4} borderTop="1px solid" borderColor="gray.200">
                        <VStack spacing={4} align="stretch">
                          <HStack justify="space-between" align="center">
                            <Heading size="sm" color="gray.700">
                              Employee Details
                            </Heading>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEmployeeClick(item);
                              }}
                            >
                              View All Information
                            </Button>
                          </HStack>
                          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                            <DetailItem label="Location" value={item.location} icon={<FiHome />} />
                            <DetailItem label="Hire Date" value={item.hire_date} icon={<FiCalendar />} />
                            <DetailItem label="Status" value={item.status || "Active"} icon={<FiUser />} />
                          </SimpleGrid>
                        </VStack>
                      </Box>
                    </Collapse>
                  </Td>
                </Tr>
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filter Employees</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              {/* Department Filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  Department
                </FormLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  placeholder="All Departments"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500" }}
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Action Buttons */}
              <HStack spacing={3} pt={4}>
                <Button
                  colorScheme="blue"
                  variant="solid"
                  onClick={onClose}
                  size="md"
                  borderRadius="8px"
                  flex={1}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    clearFilters();
                    onClose();
                  }}
                  size="md"
                  borderRadius="8px"
                  flex={1}
                >
                  Clear All
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Employee Table Row Component
const EmployeeTableRow = ({ 
  first_name,
  last_name,
  employee_id,
  email,
  phone_number,
  department,
  salary,
  profile_image,
  status,
  archived_at,
  archived_by,
  isExpanded,
  onToggle,
  onEdit,
  onView,
  refetchGetEmployees,
  planInfo
}) => {
  const { deleteEmployeePayload, deleteEmployeeIsLoading } = useDeleteEmployee(() => refetchGetEmployees());
  const { restoreEmployeePayload, restoreEmployeeIsLoading } = useRestoreEmployee(() => refetchGetEmployees());
  const fullName = `${first_name} ${last_name}`;
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    deleteEmployeePayload(employee_id);
    setShowDeleteConfirm(false);
  };

  const handleRestore = () => {
    if (window.confirm('Restore this employee? They will be able to clock in and perform operations again.')) {
      restoreEmployeePayload(employee_id);
    }
  };

  return (
    <>
      <Tr 
        cursor="pointer" 
        _hover={{ bg: "gray.50" }}
        onClick={onToggle}
        opacity={status === "archived" ? 0.7 : 1}
      >
        <Td>
          <IconButton
            icon={isExpanded ? <FiChevronDown /> : <FiChevronRight />}
            variant="ghost"
            size="sm"
            aria-label="Toggle details"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
        </Td>
        <Td>
          <HStack spacing={3}>
            <AvatarComponent name={fullName} image={profile_image} />
            <VStack align="start" spacing={0}>
              <Text fontWeight="600">{fullName}</Text>
              {status === "archived" && (
                <Badge colorScheme="gray" size="sm">Archived</Badge>
              )}
            </VStack>
          </HStack>
        </Td>
        <Td>{email}</Td>
        <Td>{phone_number}</Td>
        <Td>{employee_id}</Td>
        <Td>{department}</Td>
        <Td>{salary ? formatCurrency(salary, planInfo?.country) : "Not specified"}</Td>
        <Td>
          <HStack spacing={2} justify="center">
            <IconButton
              icon={<FiUser />}
              variant="ghost"
              size="sm"
              aria-label="View employee"
              onClick={(e) => {
                e.stopPropagation();
                onView();
              }}
              color="blue.500"
              _hover={{ bg: "blue.50" }}
            />
            {status === "archived" ? (
              <IconButton
                icon={<FiRefreshCw />}
                variant="ghost"
                size="sm"
                aria-label="Restore employee"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRestore();
                }}
                color="green.500"
                _hover={{ bg: "green.50" }}
                isLoading={restoreEmployeeIsLoading}
              />
            ) : (
              <>
                <IconButton
                  icon={<FiEdit />}
                  variant="ghost"
                  size="sm"
                  aria-label="Edit employee"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  color="green.500"
                  _hover={{ bg: "green.50" }}
                />
                <IconButton
                  icon={<FiTrash2 />}
                  variant="ghost"
                  size="sm"
                  aria-label="Archive employee"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick();
                  }}
                  isLoading={deleteEmployeeIsLoading}
                  color="red.500"
                  _hover={{ bg: "red.50" }}
                />
              </>
            )}
          </HStack>
        </Td>
      </Tr>

      {/* Archive Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Archive Employee</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Are you sure you want to archive this employee? All historical data (attendance, payroll, reports) will be preserved, but the employee will no longer be able to clock in or perform operations.
            </Text>
            <Text fontSize="sm" color="orange.600" fontWeight="600">
              ⚠️ This action can be reversed by restoring the employee later.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmDelete} isLoading={deleteEmployeeIsLoading}>
              Archive
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Detail Item Component
const DetailItem = ({ label, value, icon }) => {
  return (
    <HStack spacing={3} align="start">
      <Box color="gray.500">
        {icon}
      </Box>
      <VStack spacing={1} align="start" flex={1}>
        <Text fontSize="xs" color="gray.500" fontWeight="500">
          {label}
        </Text>
        <Text fontSize="sm" color="gray.900" fontWeight="600">
          {typeof value === 'object' && value !== null 
            ? (value.name || value.department_name || JSON.stringify(value))
            : (value || "Not available")
          }
        </Text>
      </VStack>
    </HStack>
  );
};

export default EmployeeTable;
